import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  LinkedIn,
  Download,
  Edit,
  Add,
  DragIndicator,
  Delete,
  Save,
  Refresh,
  Preview,
  ColorLens,
  FontDownload
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import axios from 'axios';

// Resume Template Components
import ModernTemplate from './templates/ModernTemplate';
import ClassicTemplate from './templates/ClassicTemplate';
import CreativeTemplate from './templates/CreativeTemplate';
import MinimalTemplate from './templates/MinimalTemplate';
import ProfessionalTemplate from './templates/ProfessionalTemplate';

// Tab Components
import ImportProfileTab from './tabs/ImportProfileTab';
import EditResumeTab from './tabs/EditResumeTab';
import CustomizeStyleTab from './tabs/CustomizeStyleTab';
import PreviewExportTab from './tabs/PreviewExportTab';

/**
 * Resume Builder Component
 * 
 * A comprehensive React component for building modern, editable visual resumes
 * from LinkedIn URLs. Features:
 * - LinkedIn profile import and data extraction
 * - 5 different resume template styles
 * - Drag-and-drop editing capabilities
 * - PDF export functionality
 * - Real-time preview and editing
 * - Section management (add/remove/reorder)
 */
const ResumeBuilder = () => {
  // State management
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [candidateData, setCandidateData] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [activeTab, setActiveTab] = useState(0);
  const [customizations, setCustomizations] = useState({
    primaryColor: '#2196f3',
    secondaryColor: '#f5f5f5',
    fontFamily: 'Arial',
    fontSize: 14,
    spacing: 'normal'
  });

  // Refs
  const resumeRef = useRef(null);

  // Available templates
  const templates = [
    { id: 'modern', name: 'Modern', component: ModernTemplate, preview: '/images/modern-preview.png' },
    { id: 'classic', name: 'Classic', component: ClassicTemplate, preview: '/images/classic-preview.png' },
    { id: 'creative', name: 'Creative', component: CreativeTemplate, preview: '/images/creative-preview.png' },
    { id: 'minimal', name: 'Minimal', component: MinimalTemplate, preview: '/images/minimal-preview.png' },
    { id: 'professional', name: 'Professional', component: ProfessionalTemplate, preview: '/images/professional-preview.png' }
  ];

  // Default resume sections order
  const defaultSections = [
    'personal',
    'summary',
    'experience',
    'education',
    'skills',
    'certifications',
    'projects'
  ];

  const [sectionOrder, setSectionOrder] = useState(defaultSections);

  /**
   * Fetch candidate data from LinkedIn URL
   */
  const fetchLinkedInData = useCallback(async () => {
    if (!linkedinUrl.trim()) {
      showNotification('Please enter a LinkedIn URL', 'warning');
      return;
    }

    // Validate LinkedIn URL format
    const linkedinPattern = /^https?:\/\/(www\.)?linkedin\.com\/in\/[\w-]+\/?$/;
    if (!linkedinPattern.test(linkedinUrl.trim())) {
      showNotification('Please enter a valid LinkedIn profile URL', 'error');
      return;
    }

    setIsLoading(true);
    try {
      // Extract username from LinkedIn URL
      const username = linkedinUrl.replace(/https?:\/\/(www\.)?linkedin\.com\/in\//, '').replace(/\/$/, '');
      
      // Fetch candidate data from backend
      const response = await axios.get(`/api/candidates/li/${username}`);
      
      if (response.data.candidate && response.data.candidate.length > 0) {
        const candidate = response.data.candidate[0];
        setCandidateData(transformCandidateData(candidate));
        showNotification('LinkedIn profile loaded successfully!', 'success');
        setActiveTab(1); // Switch to editor tab
      } else {
        showNotification('No candidate found with this LinkedIn profile', 'warning');
      }
    } catch (error) {
      console.error('Error fetching LinkedIn data:', error);
      showNotification('Error loading LinkedIn profile. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [linkedinUrl]);

  /**
   * Transform backend candidate data to resume format
   */
  const transformCandidateData = (candidate) => {
    return {
      personal: {
        name: candidate.name || '',
        email: candidate.email || '',
        phone: candidate.phone || '',
        location: candidate.currentLocation || '',
        linkedin: candidate.linkedin || '',
        profilePicture: candidate.dp || '',
        website: '',
        github: ''
      },
      summary: {
        content: candidate.headline || ''
      },
      experience: candidate.experience?.map(exp => ({
        id: Math.random().toString(36).substr(2, 9),
        company: exp.organization || '',
        position: exp.designation || '',
        location: exp.location || '',
        startDate: exp.startDate || '',
        endDate: exp.endDate || '',
        description: exp.description || '',
        skills: exp.skills || [],
        current: !exp.endDate
      })) || [],
      education: candidate.education?.map(edu => ({
        id: Math.random().toString(36).substr(2, 9),
        institution: edu.institution || '',
        degree: edu.degree || '',
        startDate: edu.startDate || '',
        endDate: edu.endDate || '',
        gpa: edu.marks || '',
        description: edu.description || ''
      })) || [],
      skills: candidate.skills?.map(skill => ({
        id: Math.random().toString(36).substr(2, 9),
        name: skill,
        level: 'Intermediate',
        category: 'Technical'
      })) || [],
      certifications: [],
      projects: [],
      currentCompany: candidate.currentCompany || '',
      currentRole: candidate.currentRole || '',
      experienceYears: candidate.experience_yrs || 0,
      compensation: candidate.compensation || 0
    };
  };

  /**
   * Handle drag and drop for sections
   */
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(sectionOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSectionOrder(items);
  };

  /**
   * Add new section item (experience, education, etc.)
   */
  const addSectionItem = (section, item) => {
    if (!candidateData) return;

    const newItem = {
      id: Math.random().toString(36).substr(2, 9),
      ...item
    };

    setCandidateData(prev => ({
      ...prev,
      [section]: [...(prev[section] || []), newItem]
    }));
  };

  /**
   * Update section item
   */
  const updateSectionItem = (section, itemId, updates) => {
    if (!candidateData) return;

    setCandidateData(prev => ({
      ...prev,
      [section]: prev[section]?.map(item =>
        item.id === itemId ? { ...item, ...updates } : item
      ) || []
    }));
  };

  /**
   * Delete section item
   */
  const deleteSectionItem = (section, itemId) => {
    if (!candidateData) return;

    setCandidateData(prev => ({
      ...prev,
      [section]: prev[section]?.filter(item => item.id !== itemId) || []
    }));
  };

  /**
   * Export resume as PDF
   */
  const exportToPDF = async () => {
    if (!resumeRef.current || !candidateData) {
      showNotification('Please load a profile first', 'warning');
      return;
    }

    setIsLoading(true);
    try {
      // Temporarily disable edit mode for clean export
      const originalEditMode = editMode;
      setEditMode(false);

      // Wait for UI to update
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(resumeRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${candidateData.personal.name || 'resume'}_resume.pdf`);
      showNotification('Resume exported successfully!', 'success');

      // Restore edit mode
      setEditMode(originalEditMode);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showNotification('Error exporting PDF. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Show notification
   */
  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  /**
   * Update customizations
   */
  const updateCustomization = (key, value) => {
    setCustomizations(prev => ({
      ...prev,
      [key]: value
    }));
  };

  /**
   * Get selected template component
   */
  const getTemplateComponent = () => {
    const template = templates.find(t => t.id === selectedTemplate);
    return template ? template.component : ModernTemplate;
  };

  const TemplateComponent = getTemplateComponent();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 0 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 'bold' }}>
          Resume Builder
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Create professional resumes from LinkedIn profiles with drag-and-drop editing
        </Typography>
      </Paper>

      {/* Main Content */}
      <Box sx={{ px: 3 }}>
        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Import Profile" />
            <Tab label="Edit Resume" disabled={!candidateData} />
            <Tab label="Customize Style" disabled={!candidateData} />
            <Tab label="Preview & Export" disabled={!candidateData} />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        {activeTab === 0 && (
          <ImportProfileTab
            linkedinUrl={linkedinUrl}
            setLinkedinUrl={setLinkedinUrl}
            fetchLinkedInData={fetchLinkedInData}
            isLoading={isLoading}
          />
        )}

        {activeTab === 1 && candidateData && (
          <EditResumeTab
            candidateData={candidateData}
            setCandidateData={setCandidateData}
            sectionOrder={sectionOrder}
            setSectionOrder={setSectionOrder}
            onDragEnd={onDragEnd}
            addSectionItem={addSectionItem}
            updateSectionItem={updateSectionItem}
            deleteSectionItem={deleteSectionItem}
            editMode={editMode}
            setEditMode={setEditMode}
          />
        )}

        {activeTab === 2 && candidateData && (
          <CustomizeStyleTab
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            templates={templates}
            customizations={customizations}
            updateCustomization={updateCustomization}
            showTemplateSelector={showTemplateSelector}
            setShowTemplateSelector={setShowTemplateSelector}
          />
        )}

        {activeTab === 3 && candidateData && (
          <PreviewExportTab
            candidateData={candidateData}
            selectedTemplate={selectedTemplate}
            customizations={customizations}
            onNotification={showNotification}
          />
        )}
      </Box>

      {/* Floating Action Buttons */}
      {candidateData && (
        <Box sx={{ position: 'fixed', bottom: 20, right: 20, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Fab color="primary" onClick={exportToPDF} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : <Download />}
          </Fab>
          <Fab color="secondary" onClick={() => setEditMode(!editMode)}>
            <Edit />
          </Fab>
        </Box>
      )}

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResumeBuilder;
